import { Input, Modal, PrimaryButton, PrimaryCardButton, RadioButtonGroup, RadioButton } from '@cb/apricot-react';
import { LinearProgress, Grid, Typography } from '@mui/material';
import StopCircle from '@mui/icons-material/StopCircle';
import { ChangeEventHandler, FC, useEffect, useState } from 'react';

import { useToasts } from 'src/hooks';
import { getValidatedSchoolName } from '../../utils';
import { useAuth } from 'src/contexts';
import { createAIForm } from 'src/apis';
import { BooleanRadio } from 'src/generic/component/form';
import { FieldRow, FieldLabel } from 'src/generic/component';
import { APP_URLS, authorizationLevelOptions } from 'src/constants';
import { useNavigate } from 'react-router-dom';

interface NewSchoolModalProps {
  isOpen?: boolean;
  onClose: VoidFunction;
  onComplete: VoidFunction;
}

const NewSchoolModal: FC<NewSchoolModalProps> = ({ isOpen, onClose, onComplete }) => {

  const navigate = useNavigate();
  const [schoolName, setSchoolName] = useState('');
  const [schoolOfferCourses, setOffersCourses] = useState<boolean | undefined>(undefined);
  const [appliedLevel, setAppliedLevel] = useState<'1' | '2' | undefined>(undefined);
  const [appliedType, setAppliedType] = useState<'ai' | 'aia' | undefined>(undefined);


  const { errorMessage, render: renderToasts, toastErrorMessage } = useToasts();

  const { userName, userEmail } = useAuth();

  const [loading, setLoading] = useState('');

  const handleChangeSchoolName: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSchoolName(event.target.value);
  };

  useEffect(() => {
    setSchoolName('');
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!schoolName || !appliedType || !appliedLevel) {
      toastErrorMessage('Please fill out the School Name.');
    } else {
      toastErrorMessage(null);
      setLoading('Creating');

      const validatedSchoolName = getValidatedSchoolName(schoolName);

      try {
        const created = await createAIForm({
          schoolName: validatedSchoolName,
          appliedType,
          appliedCategoryCd: appliedType === 'aia' ? 2 : 1,
          appliedLevel,
          creator: userName,
          creatorEmail: userEmail
        });

        if (appliedType === 'aia') {
          navigate(APP_URLS.FORM, { state: { summary: created } });
        } else {
          // Due to having to send the full specific form data to the form instead of having
          // an abstracted routing page that can grab the data from the standard ISummary record
          // we have to read out the level from the summary record and populate it into the redirection
          navigate(APP_URLS.SCHOOL, {
            state: {
              school: {
                ...created,
                level: created.appliedLevel
              }
            }
          });
        }

      } catch (error: any) {
        toastErrorMessage(error.message);
      }
      setLoading('');
    }
  };

  useEffect(() => {
    toastErrorMessage(null);
  }, [isOpen, toastErrorMessage]);

  return (
    // @ts-ignore
    <Modal
      title="Create a new School Code Form"
      actions={[
        <PrimaryButton key="submit" onClick={handleSubmit}>
          Submit
        </PrimaryButton>,
        <PrimaryCardButton key="close" onClick={onClose}>
          Close
        </PrimaryCardButton>,
      ]}
      closeButton={false}
      open={isOpen}
      onClose={onClose}
      withHeader>
      {loading && <LinearProgress />}

      {renderToasts()}

      <div className="display-flex flex-column cb-padding-top-8">
        <div>
          <Typography variant="body1" style={{ fontWeight: 700, color: "#ff0000" }}>
            <StopCircle style={{ color: "red", verticalAlign: "middle" }} /> College Board School Code requests must be submitted by the head of school or district or a school/district employee with written authorization to submit on behalf of the head of school or district. Written authorization must be included with the submission to continue processing.
          </Typography>
          <br/>
        </div>
        <FieldRow>
          <Input
            floating={false}
            label="School/Entity Name"
            required
            placeholder="School/Entity Name"
            onChange={handleChangeSchoolName}
            value={schoolName}
            validation={errorMessage ? 'error' : undefined}
            validationMsg={errorMessage ?? undefined}
          />
        </FieldRow>
        <FieldRow>
          <FieldLabel variant="body1">Is your organization a school that offers courses that can be added to a student's transcripts?</FieldLabel>
          <BooleanRadio name='schoolOfferCourses' error={''} onChange={(val) => { setOffersCourses(val); setAppliedType(val ? 'ai' : 'aia'); }} value={schoolOfferCourses} />
        </FieldRow>
        <div>
          <FieldRow>
            <FieldLabel>Choose Authorization Level</FieldLabel>
          </FieldRow>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <b>Level 1 Authorization: </b>
                Required for schools to <b>receive</b> their students’ Pre-AP, AP Exam, PSAT, SAT and other College Board
                test scores. Schools cannot administer AP, PSAT, SAT or other College Board exams, but can participate in
                the AP Course Audit, and in the Pre-AP program.
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <b>Level 2 Authorization: </b>
                Required for schools to <b>receive</b> scores as well as <b>administer</b> AP Exams and PSAT-related
                assessments and to apply to become a SAT Test Center.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Please note: </b>
                Six digit College Board AI Codes are reserved for primary and secondary education entities and their ambassadors. If your institution offers post-secondary education or scholarships please contact Code Services for details on the application process for a four digit College Board DI Code.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Code Services</b><br />
                <b>Phone:</b> 866.609.2447<br />
                <b>Email:</b> cbcodeservices@collegeboard.org
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {/* @ts-ignore */}
              <RadioButtonGroup
                name={'authorization'}
                onChange={(value) => { setAppliedLevel(value === '1' || value === '2' ? value : undefined); }}
                value={appliedLevel}
                vertical
                vSpacing="16">
                {authorizationLevelOptions.map((option) => (
                  <RadioButton key={option.value} label={option.label} value={option.value} />
                ))}
              </RadioButtonGroup>
            </Grid>
          </Grid>
        </div>
      </div>
    </Modal>
  );
};

export default NewSchoolModal;
